import { getZodConstraint } from "@conform-to/zod/v4";

/** `getZodConstraint`, minus `pattern`. Use this at EVERY call site.
 *
 *  Conform derives `pattern` from the Zod schema, and for `.email()` the
 *  client emits one while the server does not — so `getInputProps` spreads
 *  an attribute onto the markup that isn't in the server HTML, and React
 *  logs a hydration mismatch on every load of /login, /signup and
 *  /auth/reset.
 *
 *  Stripping it costs nothing: these forms render `noValidate` because
 *  Conform owns validation, so the browser never evaluates `pattern`. It is
 *  inert markup either way — and stripping it in ONE place makes the two
 *  renders agree by construction rather than by coincidence.
 *
 *  WHY HERE and not in the `Input` component, which is where this first
 *  landed: `getInputProps` emits `pattern` for whatever element you spread
 *  it onto, and consumers that are not `Input` exist — e.g. the raw
 *  `<input>` inside DocumentCheckbox. A
 *  component-level strip leaves both one `.regex()` away from the same
 *  warning, which is the exact objection that retired the per-call-site
 *  helper before it. The constraint object is the layer every consumer
 *  shares.
 *
 *  It also keeps `pattern` usable: a component-level strip silently voided
 *  `<Input pattern="[0-9]*" />`, the standard iOS numeric-keypad hint,
 *  while the prop type went on advertising it. */
export function formConstraint(
  schema: Parameters<typeof getZodConstraint>[0],
): ReturnType<typeof getZodConstraint> {
  return Object.fromEntries(
    Object.entries(getZodConstraint(schema)).map(([name, constraint]) => {
      const next = { ...constraint };
      delete next.pattern;
      return [name, next];
    }),
  );
}

/** onBlur fires Conform validation when focus moves to a sibling
 *  form's submit button (e.g. OAuth on login/signup), surfacing a
 *  spurious "required" error on a field the user wasn't trying to
 *  use. Set onSubmit explicitly — even though it's Conform's default
 *  — so a "DRY the default" cleanup can't silently revert the choice. */
export const FORM_VALIDATE_OPTIONS = {
  shouldValidate: "onSubmit",
  shouldRevalidate: "onInput",
} as const;
